import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Select, message, ConfigProvider, theme as antdTheme } from "antd";
import { getPagamentoById, postPagamento } from "@/services/pagamento";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { errorHandler } from "@/utils/errorhandler";
import { TipoPagamento } from "@/types/PagamentoType";

const { Option } = Select;

const defaultValues = {
  nome: "",
  quantidadeParcelas: 1,
  tipoPagamento: undefined,
  ativo: true,
};

export default function CadastroPagamento() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm({ defaultValues });

  useEffect(() => {
    if (isEdit && id) {
      const fetchPagamento = async () => {
        try {
          const { data } = await getPagamentoById(id);
          reset({
            nome: data.nome,
            quantidadeParcelas: data.quantidadeParcelas,
            tipoPagamento: data.tipoPagamento,
            ativo: data.ativo !== undefined ? data.ativo : true,
          });
        } catch (error) {
          message.error("Falha ao buscar dados do pagamento.");
          errorHandler(error);
        }
      };
      fetchPagamento();
    } else {
      reset(defaultValues);
    }
  }, [id, isEdit, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload = {
        ...data,
        quantidadeParcelas: Number(data.quantidadeParcelas),
        ativo: !!data.ativo,
        ...(id && { id: Number(id) }),
      };
      await postPagamento(payload);
      message.success(`Pagamento ${id ? 'atualizado' : 'cadastrado'} com sucesso!`);
      navigate("/pagamentos");
    } catch (error: any) {
      errorHandler(error);
    }
  });

  const handleCancel = useCallback(() => {
    navigate("/pagamentos");
  }, [navigate]);

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={onSubmit}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Forma de Pagamento" : "Cadastro de Forma de Pagamento"}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-2 mt-6 md:col-span-2">
            <Input
              className="h-4 w-4"
              type="checkbox"
              id="ativo"
              {...register("ativo")}
              checked={watch("ativo")}
              onChange={e => setValue("ativo", e.target.checked)}
            />
            <label htmlFor="ativo">Ativo</label>
          </div>
          <div className="md:col-span-2">
            <label htmlFor="nome">Nome *</label>
            <Input 
              type="text" 
              id="nome" 
              {...register("nome", { required: "Nome é obrigatório" })} 
            />
            {errors.nome && <span className="text-red-500 text-sm">{errors.nome.message}</span>}
          </div>
          <div>
            <label htmlFor="quantidadeParcelas">Quantidade de Parcelas *</label>
            <Input 
              type="number" 
              id="quantidadeParcelas" 
              {...register("quantidadeParcelas", { 
                required: "Quantidade de parcelas é obrigatória",
                min: { value: 1, message: "Mínimo 1 parcela" },
                max: { value: 24, message: "Máximo 24 parcelas" }
              })} 
            />
            {errors.quantidadeParcelas && <span className="text-red-500 text-sm">{errors.quantidadeParcelas.message}</span>}
          </div>
          <div>
            <label htmlFor="tipoPagamento">Tipo *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                name="tipoPagamento"
                control={control}
                rules={{ required: "Selecione um tipo" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    id="tipoPagamento"
                    style={{ width: "100%" }}
                    className="h-10"
                    placeholder="Selecione o tipo"
                  >
                    {Object.values(TipoPagamento).map((tipo) => (
                      <Option key={tipo} value={tipo}>
                        {tipo}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </ConfigProvider>
            {errors.tipoPagamento && <span className="text-red-500 text-sm">{errors.tipoPagamento.message}</span>}
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}
